"use client";

import * as X from "@/ui";
import { z } from "zod";
import { emailTs } from "@/types/form/fields";

import SuccessIcon from "@/ui/shared/assets/newsletter-success.svg";
import WarningIcon from "@/ui/shared/assets/newsletter-warning.svg";
import { Locale } from "@/config";


export function Newsletter(_props: X.PageProps) {
  const tr = X.useTranslator();
  const form = useNewsletterForm(_props.locale);

  return (
    <section className="w-full max-w-content xl:rounded-[12px] mx-auto bg-yellow py-8 xl:py-8 xl:pb-12 xl:my-12 px-mobile-margin flex flex-row">
      <div className="flex flex-col xl:flex-row gap-6 xl:gap-16 items-center flex-1">
        <div className="flex-1 text-[16px]">
          <h2 className="text-[30px] xl:text-[40px] mb-4 [&_strong]:font-medium leading-tight">
            {tr({
              en: <>Stay in the <strong>loop</strong></>,
              fr: <>Suivez nos dernières <strong>actualités</strong></>,
            })}
          </h2>
          <p className="text-[14px] xl:text-[16px] font-medium xl:max-w-[420px]">
            {tr({
              en: <>Traveling, shopping, VAT refunds: get the best tips and deals straight to your inbox.</>,
              fr: <>Voyages, shopping, détaxe : tous les bons plans dans votre boîte mail{X.Nbsp}!</>,
            })}
          </p>
          <p className="text-[14px] xl:text-[16px] font-bold">
            {tr({
              en: "Subscribe to our newsletter!",
              fr: "Inscrivez-vous à notre newsletter",
            })}
          </p>
        </div>
        <div className="flex-1">
          {form.isSent() ? (
            <div className="bg-white shadow-sm rounded-xl p-8 flex flex-col flex-1 justify-center items-center gap-4">
              <X.Image src={SuccessIcon} alt={tr({ en: "Success", fr: "Succès" })} className="w-[38px] h-auto mb-2" />
              <div className="text-[20px] font-bold text-center">{tr({ en: "Thank you!", fr: "Merci !" })}</div>
              <div className="text-[14px] font-medium text-center">{tr({
                en: "You are successfully subscribed to our newsletter",
                fr: "Nous avons bien enregistré votre inscription à notre newsletter",
              })}</div>
            </div>

          ) : (
            <form className="space-y-4" onSubmit={form.handleSubmit}>
              <div>
                <div className="relative">
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder={tr({ en: "Your e-mail", fr: "Votre e-mail" })}
                    className={X.clsx(
                      "relative w-full px-4 py-3 rounded-md bg-white border focus:outline-none focus:ring-2 focus:ring-black placeholder:text-[12px]",
                      form.hasTouchedError() ? "border-danger" : "border-white",
                    )}
                    onBlur={form.handleBlur}
                    onChange={form.handleChangeEmail}
                    value={form.getEmail()}
                    required
                  />
                  {form.hasTouchedError() && (
                    <X.Image src={WarningIcon} alt={tr({ en: "Error", fr: "Erreur" })} className="w-[24px] h-auto absolute right-4 top-1/2 -translate-y-1/2" />
                  )}
                </div>
                {form.hasTouchedError() && (
                  <p className="text-danger text-[12px] mt-1">
                    {tr({
                      en: "Please enter a valid email address",
                      fr: "Veuillez introduire un e-mail valide",
                    })}
                  </p>
                )}
              </div>
              <p className="text-[10px] xl:mb-6">
                {tr({
                  en: "By sharing your email, you agree to receive updates from Zapptax and confirm that you’ve read our privacy policy.",
                  fr: `En communiquant votre adresse e-mail, j'accepte de recevoir des informations de la part de Zapptax et je confirme avoir pris connaissance de la politique de confidentialité.`,
                })}
              </p>
              <X.Button
                secondary lg desktopMd
                disabled={!form.canSubmit()}
                processing={form.isSubmitting()}
                label={tr({ en: "Subscribe", fr: "Je m'inscris" })}
                submit
              />
            </form>
          )}
        </div>
      </div>
    </section>
  );
}


export function useNewsletterForm(locale: Locale) {
  type TaskId = {};

  type State = (
    | { sent: false; email: string; submitting: TaskId; valid: true; touched: true; error: false }
    | { sent: false; email: string; submitting: null; valid: boolean; touched: boolean; error: boolean }
    | { sent: true; }
  );

  const [blurred, setBlurred] = X.useState(false);

  const state = X.useStateRef<State>({
    sent: false,
    email: "",
    submitting: null,
    touched: false,
    valid: false,
    error: false,
  });

  const handleChangeEmail = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const email = ev.target.value;

    if (state.current.sent || state.current.submitting) {
      return;
    }

    const valid = isEmailValid(email);

    state.set({
      sent: false,
      submitting: null,
      touched: true,
      error: false,
      valid,
      email,
    });
  };

  const handleSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    if (
      false
      || state.current.sent
      || state.current.submitting
      || !state.current.touched
      || !state.current.valid
    ) {
      return;
    }

    const email = state.current.email;
    const taskId: TaskId = {};

    state.set({
      sent: false,
      submitting: taskId,
      touched: true,
      valid: true,
      error: false,
      email,
    });

    const result = await X.safeTryAsync(
      () => fetch("/api/form/subscribe-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, locale: locale }),
      })
        .then(res => res.json())
        .then(z.object({ ok: z.literal(true) }).parse)
        .then(() => true as const)
    );

    if (state.current.sent || state.current.submitting !== taskId) {
      return;
    }

    if (!result.ok) {
      state.set({
        sent: false,
        submitting: null,
        touched: true,
        valid: true,
        error: true,
        email,
      });

      return;
    }

    state.set({
      sent: true,
    });
  };

  const handleBlur = (_ev: React.FocusEvent<HTMLInputElement>) => {
    setBlurred(true);
  };

  return {
    isSent: () => state.current.sent,
    isSubmitting: () => !state.current.sent && !!state.current.submitting,
    hasTouchedError: () => !state.current.sent && state.current.touched && !state.current.valid && blurred && state.current.email.length > 0,

    canSubmit: () => !state.current.sent && state.current.valid && state.current.touched,
    getEmail: () => state.current.sent ? "" : state.current.email,

    handleChangeEmail,
    handleSubmit,
    handleBlur,
  }
}


function isEmailValid(email: string) {
  return emailTs.safeParse(email).success;
}
